import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import './Builders.css';

const Builders = () => {
    const [isLoaded, setIsLoaded] = useState(false);

    useEffect(() => {
        setIsLoaded(true);
    }, []);

    return (
        <div className={`page builders-page ${isLoaded ? 'loaded' : ''}`}>
            {/* Hero */}
            <section className="page-hero">
                <div className="container">
                    <span className="label">For Builders & General Contractors</span>
                    <h1>We handle systems complexity.<br />You deliver on time.</h1>
                    <p className="page-hero-subtitle">
                        Fewer automation-related callbacks. Clear scope from day one.
                        Documentation that protects you long after close.
                    </p>
                    <div className="page-hero-cta">
                        <Link to="/contact" className="btn btn-primary">Start a project conversation</Link>
                    </div>
                </div>
            </section>

            {/* Pain Points */}
            <section className="pain-section">
                <div className="container">
                    <div className="pain-header">
                        <span className="label">The Problem</span>
                        <h2>Technology subs create problems you shouldn't have to solve.</h2>
                    </div>

                    <div className="pain-grid">
                        <div className="pain-card">
                            <span className="pain-num">01</span>
                            <h3>Schedule disruptions</h3>
                            <p>AV and low-voltage contractors arrive late, miss coordination windows, and create cascading delays across your other trades.</p>
                        </div>
                        <div className="pain-card">
                            <span className="pain-num">02</span>
                            <h3>Post-close callbacks</h3>
                            <p>You never get warranty calls for drywall or tile. But technology? That's a different story—and it damages your reputation.</p>
                        </div>
                        <div className="pain-card">
                            <span className="pain-num">03</span>
                            <h3>Finger-pointing</h3>
                            <p>When something doesn't work, the electrician blames the AV guy, the AV guy blames the network, and you're stuck in the middle.</p>
                        </div>
                        <div className="pain-card">
                            <span className="pain-num">04</span>
                            <h3>Missing documentation</h3>
                            <p>Without coordinated drawings, electricians are left to guess at conduit runs, device locations and trade interfaces.</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Solution */}
            <section className="solution-section">
                <div className="container">
                    <div className="solution-header">
                        <span className="label">How We're Different</span>
                        <h2>One point of accountability. Complete documentation. No surprises.</h2>
                    </div>

                    <div className="solution-grid">
                        <div className="solution-card">
                            <h3>Scope clarity from day one</h3>
                            <p>
                                You get a detailed system scope document before we start. Every device,
                                every wire run, every coordination requirement—documented and approved
                                before ground breaks.
                            </p>
                        </div>
                        <div className="solution-card">
                            <h3>Trade coordination built in</h3>
                            <p>
                                We provide rough-in drawings your electricians can actually use. Wire schedules,
                                box locations, conduit paths—all coordinated with your other trades before
                                walls close.
                            </p>
                        </div>
                        <div className="solution-card">
                            <h3>Schedule reliability</h3>
                            <p>
                                We show up when we say we will. Construction projects have enough variables.
                                We're not one of them.
                            </p>
                        </div>
                        <div className="solution-card">
                            <h3>Single point of contact</h3>
                            <p>
                                No more managing three different technology subs. We own the entire
                                automation scope—lighting, shades, climate, audio and networking—as one
                                coordinated system.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Documentation Showcase */}
            <section className="docs-section">
                <div className="container">
                    <div className="docs-content">
                        <span className="label">Documentation</span>
                        <h2>The paperwork that protects you</h2>
                        <p>
                            Clear drawings and records help your trades build with fewer questions
                            and give future service providers a reliable account of the system.
                        </p>
                        <p>
                            Every system we deliver includes comprehensive documentation: rough-in
                            drawings, termination schedules, as-built records, programming backups,
                            and owner manuals. When your buyer sells in ten years, the next service
                            provider will know exactly what they're working with.
                        </p>
                    </div>
                    <div className="docs-list">
                        <h4>What you receive:</h4>
                        <ul>
                            <li>Scope of Work document</li>
                            <li>Device location plans</li>
                            <li>Rough-in drawings</li>
                            <li>Wire and conduit schedules</li>
                            <li>Trade coordination matrix</li>
                            <li>Commissioning checklist</li>
                            <li>As-built documentation</li>
                            <li>Owner operation guides</li>
                            <li>Programming backups</li>
                            <li>Warranty summary</li>
                        </ul>
                    </div>
                </div>
            </section>

            {/* Process Overview */}
            <section className="process-section">
                <div className="container">
                    <span className="label">Our Process</span>
                    <h2>How we work with your timeline</h2>

                    <div className="process-timeline">
                        <div className="process-step">
                            <span className="step-number">01</span>
                            <h3>Pre-construction</h3>
                            <p>We join your team early—during design development—to coordinate requirements before they become change orders.</p>
                        </div>
                        <div className="process-step">
                            <span className="step-number">02</span>
                            <h3>Rough-in</h3>
                            <p>Your electricians get clear drawings. We verify installation before walls close. No surprises at trim.</p>
                        </div>
                        <div className="process-step">
                            <span className="step-number">03</span>
                            <h3>Trim & equipment</h3>
                            <p>Devices installed, equipment mounted, systems tested room by room.</p>
                        </div>
                        <div className="process-step">
                            <span className="step-number">04</span>
                            <h3>Commissioning</h3>
                            <p>Full system testing, programming, and documentation—completed before your final walkthrough.</p>
                        </div>
                        <div className="process-step">
                            <span className="step-number">05</span>
                            <h3>White-glove handoff</h3>
                            <p>We train your buyer directly. They know how to use everything before you hand them the keys.</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="page-cta">
                <div className="container">
                    <h2>Let's talk about your next project.</h2>
                    <p>
                        Whether you're building one custom home or managing a portfolio of luxury
                        properties, we'd like to understand your needs.
                    </p>
                    <Link to="/contact" className="btn btn-primary">Start a conversation</Link>
                </div>
            </section>
        </div>
    );
};

export default Builders;
